<template>
  <div class="quotation-list">
    <div class="page-header">
      <h2>견적관리</h2>
      <div class="header-actions">
        <el-button type="primary" @click="openCreateDialog('QT')">
          <el-icon><Plus /></el-icon>
          견적서 작성
        </el-button>
        <el-button @click="openCreateDialog('PC')">
          <el-icon><DocumentAdd /></el-icon>
          사전원가 작성
        </el-button>
        <el-button @click="showHelp" size="small">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 필터 영역 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters">
        <el-form-item label="유형">
          <el-select v-model="filters.quotation_type" clearable placeholder="전체" style="width: 130px;">
            <el-option label="사전원가" value="PC" />
            <el-option label="견적서" value="QT" />
          </el-select>
        </el-form-item>
        <el-form-item label="브랜드">
          <el-select v-model="filters.brand" clearable placeholder="전체" filterable style="width: 160px;">
            <el-option
              v-for="brand in brands"
              :key="brand.id"
              :label="brand.name"
              :value="brand.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="상태">
          <el-select v-model="filters.status" clearable placeholder="전체" style="width: 130px;">
            <el-option label="작성중" value="DRAFT" />
            <el-option label="완료" value="COMPLETED" />
            <el-option label="확정" value="CONFIRMED" />
            <el-option label="대체됨" value="SUPERSEDED" />
            <el-option label="취소" value="CANCELLED" />
          </el-select>
        </el-form-item>
        <el-form-item label="스타일번호">
          <el-input v-model="filters.search" clearable placeholder="스타일번호 검색" style="width: 180px;" @keyup.enter="fetchData" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchData">검색</el-button>
          <el-button @click="resetFilters">초기화</el-button>
          <el-button :type="showAll ? '' : 'warning'" @click="toggleShowAll" size="small">
            {{ showAll ? '스마트뷰' : '전체보기' }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 데이터 테이블 -->
    <el-card class="table-card">
      <el-table
        :data="store.quotations"
        v-loading="store.loading"
        @row-click="handleRowClick"
        stripe
        highlight-current-row
        table-layout="auto"
      >
        <el-table-column label="유형" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.quotation_type === 'PC' ? 'warning' : 'primary'" size="small">
              {{ row.quotation_type_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="quotation_number" label="견적번호" min-width="140" />
        <el-table-column prop="brand_name" label="브랜드" min-width="110" />
        <el-table-column prop="design_style_number" label="스타일번호" min-width="130" />
        <el-table-column prop="manufacturer_name" label="공장" min-width="100" />
        <el-table-column label="발주수량" min-width="100" align="right">
          <template #default="{ row }">
            {{ row.order_quantity?.toLocaleString() }}
          </template>
        </el-table-column>
        <el-table-column v-if="canViewCosts" label="견적가" min-width="120" align="right">
          <template #default="{ row }">
            {{ formatCurrency(row.quotation_price) }}
          </template>
        </el-table-column>
        <el-table-column v-if="canViewCosts" label="PCS단가" min-width="110" align="right">
          <template #default="{ row }">
            {{ formatCurrency(row.effective_unit_price) }}
          </template>
        </el-table-column>
        <el-table-column label="상태" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ row.status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="request_date" label="요청일" min-width="110" />
        <el-table-column label="입력" width="70" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.is_auto" size="small" effect="dark" color="#1f3a5f" style="border:none"
                    :title="`자동 변환 — 원본: ${row.auto_origin?.source_file || ''}${row.auto_origin?.sheet_name ? ' / ' + row.auto_origin.sheet_name : ''}`">자동</el-tag>
            <span v-else style="color:#909399;font-size:11px">직접</span>
          </template>
        </el-table-column>
        <el-table-column label="" width="50" fixed="right">
          <template #default="{ row }">
            <el-tooltip content="복사" placement="top">
              <el-button size="small" :icon="CopyDocument" circle @click.stop="openCopyDialog(row)" />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[20, 50, 100]"
        :total="store.totalCount"
        layout="total, sizes, prev, pager, next"
        @size-change="fetchData"
        @current-change="fetchData"
        style="margin-top: 20px; justify-content: flex-end;"
      />
    </el-card>

    <!-- 생성 다이얼로그 -->
    <el-dialog v-model="showCreateDialog" :title="createDialogTitle" width="650px">
      <el-form :model="createForm" label-width="120px">
        <!-- 샘플 진입 안내 -->
        <el-alert
          v-if="createForm.import_from_sample"
          type="success"
          :closable="false"
          show-icon
          style="margin-bottom: 12px"
          :title="`샘플 ${fromSampleNumber || ''} 의 자재 목록을 가져와 견적서를 만듭니다`"
        />
        <!-- 복사 원본 미리보기 -->
        <div v-if="copySource" class="copy-source-preview">
          <div class="copy-source-header">
            <el-icon><CopyDocument /></el-icon>
            <span>복사 원본: <strong>{{ copySource.quotation_number }}</strong></span>
            <el-button size="small" text type="danger" @click="clearCopySource">해제</el-button>
          </div>
          <el-descriptions :column="3" size="small" border>
            <el-descriptions-item label="브랜드">{{ copySource.brand_name }}</el-descriptions-item>
            <el-descriptions-item label="스타일번호">{{ copySource.design_style_number }}</el-descriptions-item>
            <el-descriptions-item label="공장">{{ copySource.manufacturer_name || '-' }}</el-descriptions-item>
            <el-descriptions-item v-if="canViewCosts" label="견적가">{{ formatCurrency(copySource.quotation_price) }}</el-descriptions-item>
            <el-descriptions-item v-if="canViewCosts" label="PCS단가">{{ formatCurrency(copySource.effective_unit_price) }}</el-descriptions-item>
            <el-descriptions-item label="발주수량">{{ copySource.order_quantity?.toLocaleString() }}</el-descriptions-item>
          </el-descriptions>
          <div class="copy-source-note">
            자재/CMT/경비 정보가 원본에서 복사됩니다. 아래 항목은 새 견적서에 적용됩니다.
          </div>
        </div>

        <el-form-item label="브랜드" required>
          <el-select v-model="createForm.brand" filterable placeholder="브랜드 선택">
            <el-option
              v-for="brand in brands"
              :key="brand.id"
              :label="brand.name"
              :value="brand.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="디자인" required>
          <el-select v-model="createForm.design" filterable remote :remote-method="searchDesigns" placeholder="스타일번호 검색">
            <el-option
              v-for="d in designs"
              :key="d.id"
              :label="`${d.style_number || d.main_style_number || ''} - ${d.style_name || ''}`"
              :value="d.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="발주수량" required>
          <el-input-number v-model="createForm.order_quantity" :min="1" />
        </el-form-item>
        <el-form-item label="견적요청일" required>
          <el-date-picker v-model="createForm.request_date" type="date" value-format="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="납기일">
          <el-date-picker v-model="createForm.delivery_date" type="date" value-format="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="공장">
          <el-select v-model="createForm.manufacturer" clearable filterable placeholder="공장 선택">
            <el-option
              v-for="s in manufacturers"
              :key="s.id"
              :label="s.name"
              :value="s.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="환율">
          <el-input-number v-model="createForm.exchange_rate" :precision="2" :step="10" :min="0" />
        </el-form-item>
        <el-form-item label="마진율(%)">
          <el-input-number v-model="createForm.margin_rate" :precision="2" :step="1" :min="0" :max="100" />
        </el-form-item>

        <!-- 과거 견적 복사 검색 (복사 원본 미지정 시에만 표시) -->
        <el-form-item v-if="!copySource" label="과거 견적 복사">
          <el-select
            v-model="createForm.copy_from"
            clearable
            filterable
            remote
            :remote-method="searchForCopy"
            :loading="store.searchLoading"
            placeholder="스타일번호/견적번호로 검색"
            @change="handleCopyFromChange"
            style="width: 100%;"
          >
            <el-option
              v-for="item in store.searchResults"
              :key="item.id"
              :label="`${item.quotation_number} - ${item.design_style_number} (${item.brand_name})`"
              :value="item.id"
            >
              <div class="copy-option">
                <span class="copy-option-main">{{ item.quotation_number }} - {{ item.design_style_number }}</span>
                <span class="copy-option-sub">{{ item.brand_name }} | {{ item.order_quantity?.toLocaleString() }}pcs</span>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCreateDialog = false">취소</el-button>
        <el-button type="primary" @click="submitCreate" :loading="creating">생성</el-button>
      </template>
    </el-dialog>

    <HelpPanel v-model="helpVisible" :content-path="'screens/quotation-list'" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Plus, DocumentAdd, CopyDocument, QuestionFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useEstimateStore } from '@/stores/estimateStore'
import { useUserStore } from '@/stores/user'
import HelpPanel from '@/components/help/HelpPanel.vue'

const router = useRouter()
const route = useRoute()
const store = useEstimateStore()
const userStore = useUserStore()

// 원가/마진 필드 조회 권한
const canViewCosts = computed(() => userStore.hasQuotationAccess || userStore.hasCostAccess)

const brands = ref([])
const designs = ref([])
const manufacturers = ref([])
const showCreateDialog = ref(false)
const creating = ref(false)
const copySource = ref(null) // 복사 원본 견적 전체 정보
const showAll = ref(false)
const helpVisible = ref(false)
const showHelp = () => { helpVisible.value = true }

const filters = reactive({
  quotation_type: null,
  brand: null,
  status: null,
  search: ''
})

const pagination = reactive({
  page: 1,
  pageSize: 20
})

const createForm = reactive({
  quotation_type: 'QT',
  brand: null,
  design: null,
  order_quantity: 1000,
  request_date: new Date().toISOString().split('T')[0],
  delivery_date: null,
  manufacturer: null,
  exchange_rate: 1380,
  margin_rate: 10,
  copy_from: null,
  import_from_sample: null, // 샘플→견적 이어주기 (샘플 자재 자동 import)
})
// 샘플에서 넘어온 진입 표시 (다이얼로그 안내용)
const fromSampleNumber = ref('')

const createDialogTitle = computed(() => {
  const base = createForm.quotation_type === 'PC' ? '사전원가 작성' : '견적서 작성'
  return copySource.value ? `${base} (복사)` : base
})

const getStatusType = (status) => {
  const types = {
    DRAFT: 'info',
    MATERIAL_DONE: '',
    CMT_DONE: '',
    EXPENSE_DONE: '',
    COMPLETED: 'success',
    CONFIRMED: 'warning',
    SUPERSEDED: 'info',
    CANCELLED: 'danger'
  }
  return types[status] || 'info'
}

const formatCurrency = (amount) => {
  if (!amount) return '-'
  return Number(amount).toLocaleString() + '원'
}

const fetchData = async () => {
  const params = {
    page: pagination.page,
    page_size: pagination.pageSize
  }
  if (filters.quotation_type) params.quotation_type = filters.quotation_type
  if (filters.brand) params.brand = filters.brand
  if (filters.status) params.status = filters.status
  if (filters.search) params.search = filters.search

  // Remove empty filters
  Object.keys(params).forEach((key) => {
    if (!params[key]) {
      delete params[key]
    }
  })

  // Smart filter: exclude SUPERSEDED, CANCELLED when showAll is false
  if (!showAll.value && !params.status) {
    params.status__in = ['DRAFT', 'COMPLETED', 'CONFIRMED']
  }

  await store.fetchQuotations(params)
}

const resetFilters = () => {
  filters.quotation_type = null
  filters.brand = null
  filters.status = null
  filters.search = ''
  pagination.page = 1
  showAll.value = false
  fetchData()
}

const toggleShowAll = () => {
  showAll.value = !showAll.value
  pagination.page = 1
  fetchData()
}

const handleRowClick = (row) => {
  router.push(`/quotations/${row.id}`)
}

const resetCreateForm = (type) => {
  createForm.quotation_type = type
  createForm.brand = null
  createForm.design = null
  createForm.order_quantity = 1000
  createForm.request_date = new Date().toISOString().split('T')[0]
  createForm.delivery_date = null
  createForm.manufacturer = null
  createForm.exchange_rate = 1380
  createForm.margin_rate = 10
  createForm.copy_from = null
  createForm.import_from_sample = null
  fromSampleNumber.value = ''
  copySource.value = null
}

const openCreateDialog = (type) => {
  resetCreateForm(type)
  showCreateDialog.value = true
}

// 목록에서 "복사" 버튼 클릭 → 원본 정보 채워서 다이얼로그 열기
const openCopyDialog = (row) => {
  resetCreateForm(row.quotation_type)
  copySource.value = row
  createForm.copy_from = row.id
  createForm.brand = row.brand
  createForm.design = row.design
  createForm.manufacturer = row.manufacturer
  // 디자인 select에 원본 디자인 표시를 위해 옵션 추가
  if (row.design) {
    const existing = designs.value.find(d => d.id === row.design)
    if (!existing) {
      designs.value = [{ id: row.design, style_number: row.design_internal_number, main_style_number: row.design_style_number, style_name: '' }, ...designs.value]
    }
  }
  showCreateDialog.value = true
}

// 과거 견적 검색에서 선택 시 자동 채우기
const handleCopyFromChange = (selectedId) => {
  if (!selectedId) {
    clearCopySource()
    return
  }
  const selected = store.searchResults.find(q => q.id === selectedId)
  if (selected) {
    copySource.value = selected
    createForm.brand = selected.brand
    createForm.design = selected.design
    createForm.manufacturer = selected.manufacturer
    // 디자인 select에 옵션 추가
    if (selected.design) {
      const existing = designs.value.find(d => d.id === selected.design)
      if (!existing) {
        designs.value = [{ id: selected.design, style_number: selected.design_internal_number, main_style_number: selected.design_style_number, style_name: '' }, ...designs.value]
      }
    }
  }
}

const clearCopySource = () => {
  copySource.value = null
  createForm.copy_from = null
}

const searchDesigns = async (keyword) => {
  if (!keyword || keyword.length < 1) return
  try {
    const response = await axiosInstance.get('designs/', {
      params: { search: keyword, page_size: 20 }
    })
    designs.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to search designs:', error)
  }
}

const searchForCopy = async (keyword) => {
  if (!keyword || keyword.length < 1) return
  await store.searchQuotations({ design_keyword: keyword })
}

const submitCreate = async () => {
  if (!createForm.brand || !createForm.design) {
    ElMessage.warning('브랜드와 디자인을 선택하세요.')
    return
  }
  creating.value = true
  try {
    const data = { ...createForm }
    if (!data.copy_from) delete data.copy_from
    if (!data.delivery_date) delete data.delivery_date
    if (!data.manufacturer) delete data.manufacturer
    if (!data.import_from_sample) delete data.import_from_sample
    const result = await store.createQuotation(data)
    ElMessage.success(
      data.import_from_sample ? '샘플 자재를 가져와 견적서를 생성했습니다.'
        : copySource.value ? '견적서가 복사 생성되었습니다.' : '견적서가 생성되었습니다.'
    )
    showCreateDialog.value = false
    router.push(`/quotations/${result.id}`)
  } catch (error) {
    console.error('Failed to create quotation:', error)
  } finally {
    creating.value = false
  }
}

const fetchBrands = async () => {
  try {
    const response = await axiosInstance.get('brands/')
    brands.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch brands:', error)
  }
}

const fetchManufacturers = async () => {
  try {
    const response = await axiosInstance.get('production/manufacturers/', { params: { active: true } })
    manufacturers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch manufacturers:', error)
  }
}

// 샘플 → 견적 이어주기: /quotations?design=&brand=&sample=&sampleNo= 진입 시
// 견적서 작성 다이얼로그 자동 오픈 + 샘플 자재 import 예약
const openFromSampleIfNeeded = async () => {
  const q = route.query
  if (!q.design || !q.sample) return
  resetCreateForm('QT')
  createForm.design = Number(q.design)
  if (q.brand) createForm.brand = Number(q.brand)
  createForm.import_from_sample = Number(q.sample)
  fromSampleNumber.value = q.sampleNo || ''
  // 디자인 select 옵션 보강 (검색 전이라 목록에 없을 수 있음)
  if (!designs.value.find(d => d.id === createForm.design)) {
    try {
      const res = await axiosInstance.get(`designs/${createForm.design}/`)
      designs.value = [{
        id: res.data.id, style_number: res.data.style_number,
        main_style_number: res.data.main_style_number, style_name: res.data.product_name,
      }, ...designs.value]
      if (!createForm.brand && res.data.brand) createForm.brand = res.data.brand
    } catch { /* 무시 */ }
  }
  showCreateDialog.value = true
  // 쿼리 정리 (새로고침·뒤로가기 시 재오픈 방지) — router.replace 로 하면 App.vue keep-alive 가
  // :key="route.fullPath" 라 컴포넌트가 새로 마운트되어 방금 연 다이얼로그가 사라진다 (2026-10-18 투어 감사에서 발견).
  // 라우터를 거치지 않고 주소만 바꾼다.
  try { window.history.replaceState(window.history.state, '', route.path) } catch (e) { /* 무시 */ }
}

onMounted(async () => {
  await Promise.all([fetchData(), fetchBrands(), fetchManufacturers()])
  openFromSampleIfNeeded()
})
</script>

<style scoped>
.quotation-list {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.el-table {
  cursor: pointer;
}

/* 복사 원본 미리보기 */
.copy-source-preview {
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.copy-source-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #67c23a;
}

.copy-source-note {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

/* 검색 옵션 스타일 */
.copy-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.copy-option-main {
  font-size: 13px;
}

.copy-option-sub {
  font-size: 12px;
  color: #909399;
}
</style>
